import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { IndexConfigService } from './index.config.service';
import { Load } from '../_decorators/loading.decorator';
import { Integration } from '../_models/integration';
import { LoginHelper } from '../helpers';
import { ApiConnectionConfig } from '../_models/apiConnectionConfig';

@Injectable()
export class IntegrationsService {
  constructor(
    public http: HttpClient,
    public authenticationService: AuthenticationService,
    public config: IndexConfigService
  ) {}

  @Load()
  getAll(): Observable<Integration[]> {
    if (!this.canExecute()) {
      return null;
    }

    return this.http
      .get<Integration[]>(this.config.api + '/api/integrations')
      .pipe(
        map((response) => {
          return response.map(this.mapIntegration);
        })
      );
  }

  @Load()
  getById(id: number): Observable<Integration> {
    if (!this.canExecute()) {
      return null;
    }

    return this.http
      .get<Integration>(this.config.api + `/api/integrations/${id}`)
      .pipe(map(this.mapIntegration));
  }

  @Load()
  add(integration: Integration): Observable<Integration> {
    if (!this.canExecute()) {
      return null;
    }

    const payload = {
      ...integration,
      settings: integration.settings
        ? JSON.stringify(integration.settings)
        : undefined,
      connectionSettings: integration.connectionSettings ? JSON.stringify(integration.connectionSettings) : undefined,
    };

    return this.http
      .post<Integration>(this.config.api + `/api/integrations`, payload)
      .pipe(map(this.mapIntegration));
  }

  @Load()
  update(integration: Integration) {
    if (!this.canExecute()) {
      return null;
    }
    const payload = {
      ...integration,
      settings: integration.settings
        ? JSON.stringify(integration.settings)
        : undefined,
      connectionSettings: integration.connectionSettings ? JSON.stringify(integration.connectionSettings) : undefined,
    };

    return this.http.patch<Integration>(
      this.config.api + `/api/integrations/${integration.id}`,
      payload
    );
  }

  @Load()
  delete(id: number) {
    if (!this.canExecute()) {
      return null;
    }

    return this.http.delete(this.config.api + `/api/integrations/${id}`);
  }

  @Load()
  test(integration: Integration) {
    return this.http.post(
      this.config.api + `/api/integrations/${integration.id}/test`,
      integration
    );
  }

  @Load()
  tryExecute(apiConnectionConfig: ApiConnectionConfig) {
    return this.http.post(
      this.config.api + `/api/integrations/tryExecute`,
      apiConnectionConfig
    );
  }

  private canExecute() {
    if (
      LoginHelper.getTenantName() == null ||
      LoginHelper.getUserToken() == null
    ) {
      this.authenticationService.logout();

      return false;
    }

    return true;
  }

  private mapIntegration(integration: Integration) {
    let data = { ...integration };

    if (integration?.schedule) {
      const startDateTime = !isNaN(
        Date.parse(integration.schedule.startDateTime as any as string)
      )
        ? new Date(integration.schedule.startDateTime)
        : integration.schedule.startDateTime;
      const endDateTime = !isNaN(
        Date.parse(integration.schedule.endDateTime as any as string)
      )
        ? new Date(integration.schedule.endDateTime)
        : integration.schedule.endDateTime;

      const schedule = { ...integration.schedule, startDateTime, endDateTime };

      data.schedule = schedule;
    }

    if (integration?.settings) {
      data.settings = JSON.parse(integration.settings);
    }
    if (integration?.connectionSettings) {
      data.connectionSettings = JSON.parse(integration.connectionSettings);
    }

    return data;
  }
}
